/**
 * 移动端表单组件
 * Feature: mobile-responsive-framework, Task 8: 实现移动端表单组件
 * 需求: 1.5, 6.1, 6.5
 */
import React, { useEffect, useRef } from 'react';
import { Form, Button, Space } from 'antd';
import type { FormProps, FormInstance, Rule } from 'antd';
import { useResponsiveConfig } from '../hooks/useResponsiveConfig';
import { useTouchOptimization } from '../hooks/useTouchOptimization';
import '../styles/responsive.css';

// 提交按钮配置
export interface SubmitButtonConfig {
  text: string;
  fixed?: boolean;      // 是否固定在底部
  fullWidth?: boolean;  // 是否全宽
  loading?: boolean;
  disabled?: boolean;
}

// 取消按钮配置
export interface CancelButtonConfig {
  text: string;
  onClick: () => void;
  disabled?: boolean;
}

// 表单字段配置
export interface FormFieldConfig {
  name: string | string[];
  label: React.ReactNode;
  rules?: Rule[];
  extra?: React.ReactNode;
  children: React.ReactNode;
  hidden?: boolean;
  dependencies?: string[];
}

export interface MobileFormProps<T = any> extends Omit<FormProps<T>, 'layout'> {
  children?: React.ReactNode;
  
  // 支持 fields 配置方式
  fields?: FormFieldConfig[];
  
  // 布局配置
  layout?: 'vertical' | 'horizontal';
  labelAlign?: 'left' | 'top';
  
  // 提交按钮配置
  submitButton?: SubmitButtonConfig;
  submitText?: string;  // 简化的提交按钮文本
  submitLoading?: boolean;  // 简化的加载状态
  cancelButton?: CancelButtonConfig;
  onCancel?: () => void;  // 简化的取消回调
  
  // 移动端优化
  autoScrollToError?: boolean;
  keyboardAware?: boolean;
  
  // 表单实例回调
  onFormInstanceReady?: (form: FormInstance) => void;
  
  // 样式配置
  className?: string;
  style?: React.CSSProperties;
}

/**
 * 移动端表单组件
 * 提供移动端优化的表单布局和交互
 */
export function MobileForm<T = any>({
  children,
  fields,
  layout,
  labelAlign = 'top',
  submitButton,
  submitText,
  submitLoading,
  cancelButton,
  onCancel,
  autoScrollToError = true,
  keyboardAware = true,
  className = '',
  style = {},
  form: externalForm,
  onFinish,
  onFinishFailed,
  onFormInstanceReady,
  ...restProps
}: MobileFormProps<T>) {
  const { isMobile, isTablet, formLayout } = useResponsiveConfig();
  const { getInputProps, getButtonProps } = useTouchOptimization();
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 通知父组件表单实例已准备好
  useEffect(() => {
    if (onFormInstanceReady && form) {
      onFormInstanceReady(form);
    }
  }, [form, onFormInstanceReady]);
  
  // 计算实际布局
  const actualLayout = layout || (isMobile ? 'vertical' : formLayout);
  
  // 构建提交按钮配置
  const actualSubmitButton = submitButton || (submitText ? {
    text: submitText,
    loading: submitLoading,
    fixed: false  // 在 Modal 中不使用固定底部按钮
  } : undefined);
  
  // 构建取消按钮配置
  const actualCancelButton = cancelButton || (onCancel ? {
    text: '取消',
    onClick: onCancel
  } : undefined);
  
  // 是否显示固定底部按钮（在 Modal 中禁用）
  const showFixedBottom = false;  // 禁用固定底部按钮，避免在 Modal 中显示问题
  
  // 处理表单提交失败，滚动到错误字段
  const handleFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    if (autoScrollToError && errorInfo.errorFields.length > 0) {
      const firstErrorField = errorInfo.errorFields[0];
      const fieldName = Array.isArray(firstErrorField.name) 
        ? firstErrorField.name.join('_') 
        : firstErrorField.name;
      
      // 滚动到错误字段
      const errorElement = document.querySelector(`[id="${fieldName}"], [name="${fieldName}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // 聚焦到错误字段
        (errorElement as HTMLElement).focus?.();
      }
    }
    
    onFinishFailed?.(errorInfo);
  };
  
  // 键盘弹出时调整视图
  useEffect(() => {
    if (!keyboardAware || !isMobile) return;
    
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // 延迟滚动，等待键盘弹出
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };
    
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, [keyboardAware, isMobile]);
  
  // 渲染提交按钮区域
  const renderButtons = () => {
    if (!actualSubmitButton && !actualCancelButton) return null;
    
    const buttonProps = getButtonProps();
    
    const buttons = (
      <Space 
        orientation={isMobile ? 'vertical' : 'horizontal'} 
        style={{ width: isMobile ? '100%' : 'auto' }}
        size={isMobile ? 12 : 8}
      >
        {actualCancelButton && (
          <Button
            {...buttonProps}
            onClick={actualCancelButton.onClick}
            disabled={actualCancelButton.disabled}
            style={isMobile ? { width: '100%', ...buttonProps.style } : buttonProps.style}
          >
            {actualCancelButton.text}
          </Button>
        )}
        {actualSubmitButton && (
          <Button
            {...buttonProps}
            type="primary"
            htmlType="submit"
            loading={actualSubmitButton.loading}
            disabled={actualSubmitButton.disabled}
            style={isMobile ? { width: '100%', ...buttonProps.style } : buttonProps.style}
          >
            {actualSubmitButton.text}
          </Button>
        )}
      </Space>
    );
    
    if (showFixedBottom) {
      return (
        <div className="mobile-fixed-bottom">
          {buttons}
        </div>
      );
    }
    
    return (
      <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
        {buttons}
      </Form.Item>
    );
  };
  
  // 渲染表单字段（从 fields 配置）
  const renderFields = () => {
    if (!fields || fields.length === 0) return null;
    
    // 先过滤隐藏字段，避免返回 null 导致的 key 问题
    return fields
      .filter(field => !field.hidden)
      .map((field, index) => {
        const fieldKey = Array.isArray(field.name) 
          ? field.name.join('.') 
          : String(field.name);
        
        return (
          <Form.Item
            key={fieldKey}
            name={field.name}
            label={field.label}
            rules={field.rules}
            extra={field.extra}
            dependencies={field.dependencies}
          >
            {field.children}
          </Form.Item>
        );
      });
  };
  
  // 构建类名
  const formClassName = [
    'mobile-form',
    isMobile && 'touch-optimized',
    showFixedBottom && 'has-fixed-bottom',
    className
  ].filter(Boolean).join(' ');
  
  // 构建表单配置
  const formConfig: FormProps = {
    layout: actualLayout,
    labelCol: actualLayout === 'horizontal' ? { span: 4 } : undefined,
    wrapperCol: actualLayout === 'horizontal' ? { span: 20 } : undefined,
    labelAlign: actualLayout === 'horizontal' ? 'right' : undefined,
    size: isMobile || isTablet ? 'large' : 'middle',
    scrollToFirstError: autoScrollToError,
    ...restProps
  };
  
  return (
    <div ref={containerRef} className={formClassName} style={style}>
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={handleFinishFailed}
        {...formConfig}
      >
        {fields ? renderFields() : children}
        {renderButtons()}
      </Form>
    </div>
  );
}

export default MobileForm;
